import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../common/Icon';
import { ModalWrapper, ModalCloseButton, Button } from '../common';
import { addressSearchService } from '../../api/services';

const SEARCH_DELAY = 250; // debounce ms — 짧게 잡고 IME 조합 중인지는 별도 체크 (#18)

/**
 * Address search input with portal-based dropdown.
 * Replaces Select dropdown for destination/departure fields.
 *
 * @param {string}   value       - currently selected display name
 * @param {function} onChange    - callback(value, displayName)
 * @param {string}   placeholder - input placeholder (default: "장소 검색")
 * @param {string}   className   - wrapper class
 * @param {string}   variant     - 'default' | 'borderless'
 * @param {string}   mode        - 'dropdown' (portal, default) | 'inline' (results below input, for modals)
 * @param {object}   restriction - optional { message: string, allowGyeonggi: boolean, allowIncheon: boolean }
 */
export default function AddressSearch({
  value = '',
  displayName = '',
  onChange,
  placeholder,
  className = '',
  variant = 'default',
  mode = 'dropdown',
  restriction,
  fetchAdmin,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const effectivePlaceholder = placeholder || t('common.search_place');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedName, setSelectedName] = useState(displayName || '');
  const [dropStyle, setDropStyle] = useState({});
  const [showRestrictionModal, setShowRestrictionModal] = useState(false);
  const [mobileFullscreen, setMobileFullscreen] = useState(false);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const searchIdRef = useRef(0);

  /* ── 주소 검색 (debounced) — USE_DUMMY에 따라 카카오 or 더미 ── */
  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setSearching(false);
      return;
    }
    // 한글 자모 조합 중인지 체크 (마지막 글자가 미완성 자모면 검색 보류)
    const lastChar = trimmed[trimmed.length - 1];
    const isComposing = /[ㄱ-ㅎㅏ-ㅣ]/.test(lastChar);
    if (isComposing) {
      return;
    }
    // 쿼리 변경 즉시: ID 갱신 + 검색 중 표시 (이전 결과는 유지하여 깜빡임 방지)
    const id = ++searchIdRef.current;
    setSearching(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // #18 streaming: onPartial 로 정적 사전 결과 먼저 노출.
      //   - ko UI: 모든 partial 즉시 표시 (어차피 원문 한국어가 답)
      //   - 외국어 UI: 정적 사전 hit 항목만 노출 (nameI18n !== name).
      //     미번역 항목은 한국어 깜빡임 회피를 위해 final await 까지 보류.
      const lang = i18n.language || 'ko';
      const isForeign = lang !== 'ko';
      addressSearchService.search(query, {
        fetchAdmin,
        onPartial: (partial) => {
          if (id !== searchIdRef.current) return;
          if (partial.length === 0) return;
          if (isForeign) {
            const hits = partial.filter((p) => p.nameI18n && p.nameI18n !== p.name);
            if (hits.length === 0) return; // 모두 미번역 — final 응답 대기 (한국어 깜빡임 없음)
            setSearching(false);
            setResults(hits);
            return;
          }
          setSearching(false);
          setResults(partial);
        },
      })
        .then((data) => {
          if (id !== searchIdRef.current) return;
          // 카카오가 0개 응답 시 이전 결과 유지 (깜빡임 방지)
          if (data.length === 0) {
            setSearching(false);
            return;
          }
          setSearching(false);
          setResults(data);
        })
        .catch((e) => {
          if (id !== searchIdRef.current) return;
          console.warn('[AddressSearch] 검색 실패:', e.message);
          setSearching(false);
        });
    }, SEARCH_DELAY);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const isInline = mode === 'inline';

  /* ── Position dropdown (portal mode only) ── */
  const updatePosition = useCallback(() => {
    if (isInline) return;
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setDropStyle({
        top: r.bottom + 6,
        left: r.left,
        width: r.width,
      });
    }
  }, [isInline]);

  const isMobile = () => window.innerWidth < 1024;

  /* ── Open dropdown (모바일: 전체화면, PC: 토글 드롭다운) ── */
  const handleFocus = () => {
    if (isMobile()) {
      setMobileFullscreen(true);
      setQuery('');
      setResults([]);
      setTimeout(() => mobileInputRef.current?.focus(), 100);
      return;
    }
    if (open) {
      setOpen(false);
      return;
    }
    updatePosition();
    setOpen(true);
  };

  /* ── Input change ── */
  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (!open) {
      updatePosition();
      setOpen(true);
    }
  };

  /* ── Select a result ── */
  const handleSelect = (item) => {
    if (restriction) {
      const isSeoul = item.address.startsWith('서울');
      const isGyeonggi = item.address.startsWith('경기');
      const isIncheon = item.address.startsWith('인천');
      const isGangwon = item.address.startsWith('강원');
      let allowed = isSeoul;
      if (restriction.allowGyeonggi) allowed = allowed || isGyeonggi;
      if (restriction.allowIncheon) allowed = allowed || isIncheon;
      if (restriction.allowGangwon) allowed = allowed || isGangwon;
      if (!allowed) {
        setOpen(false);
        setShowRestrictionModal(true);
        return;
      }
    }
    setQuery('');
    // #105: input 표시용은 번역본 우선, BE에 전달하는 값은 원본 한글 유지
    setSelectedName(item.nameI18n || item.name);
    setOpen(false);
    // #216: 3번째 인자로 현지화 명칭 전달(표시 전용). BE/어드민 전달값(2번째)은 #105대로 한글 유지.
    onChange?.(item.address, item.name, item.nameI18n || item.name);
  };

  /* ── Clear input ── */
  const handleClear = (e) => {
    e.stopPropagation();
    setQuery('');
    setSelectedName('');
    onChange?.('', '');
    inputRef.current?.focus();
  };

  /* ── Outside click close ── */
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      const inTrigger = triggerRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inTrigger && !inDropdown) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  /* ── Scroll / resize close (exclude internal dropdown scroll) ── */
  useEffect(() => {
    if (!open) return;
    const handleScroll = (e) => {
      /* 드롭다운 내부 스크롤은 무시 */
      if (dropdownRef.current?.contains(e.target)) return;
      /* 트리거(입력 영역) 내부 스크롤도 무시 */
      if (triggerRef.current?.contains(e.target)) return;
      /* 부모 스크롤 컨테이너 내부 스크롤도 무시 (모달 안에서 사용 시) */
      if (e.target?.closest?.('.overflow-y-auto, .overflow-auto, [style*="overflow"]')) {
        if (triggerRef.current?.closest?.('.overflow-y-auto, .overflow-auto, [style*="overflow"]') === e.target.closest('.overflow-y-auto, .overflow-auto, [style*="overflow"]')) return;
      }
      setOpen(false);
    };
    const close = () => setOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  /* ── Escape key ── */
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  /* BugHerd #87 회귀 fix: 다국어 전환 시 이전 언어에서 검색/선택한 값이 input/드롭다운에
     그대로 남아있던 문제. selectedName(클릭 후 표시되는 번역 라벨) 뿐 아니라
     query(현재 타이핑 중인 값) / results(이전 언어로 받은 검색 결과)까지 모두 초기화.
     사용자가 재검색하면 새 언어 nameI18n 으로 다시 채움. */
  useEffect(() => {
    setSelectedName('');
    setQuery('');
    setResults([]);
    setOpen(false);
  }, [i18n.language]);

  /* ── Display value in input: 장소명 우선, 없으면 주소 ── */
  const displayValue = open ? query : (selectedName || displayName || value || '');

  return (
    <div className={`relative ${className}`} ref={triggerRef}>
      {/* Input trigger */}
      <div
        className={`flex items-center w-full transition ${
          variant === 'borderless'
            ? 'bg-transparent border-0'
            : `bg-white border rounded-lg ${
                open
                  ? 'border-orange-500 ring-1 ring-orange-500/20'
                  : 'border-mono-200 hover:border-mono-300'
              }`
        } h-12 px-4`}
      >
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          placeholder={effectivePlaceholder}
          onFocus={handleFocus}
          onClick={handleFocus}
          onChange={handleInputChange}
          className="flex-1 bg-transparent outline-none text-[15px] font-medium text-mono-900 placeholder:text-mono-500 min-w-0"
        />

        {/* Place icon (always visible when no query) */}
        {!displayValue && (
          <Icon
            name="place"
            size={18}
            className="text-mono-400 flex-shrink-0 ml-2"
          />
        )}

        {/* Clear button (visible when there's text) */}
        {displayValue && (
          <button
            type="button"
            onClick={handleClear}
            className="flex-shrink-0 ml-2 p-0.5 rounded-full hover:bg-mono-100 transition-colors"
          >
            <Icon name="close" size={16} className="text-mono-400" />
          </button>
        )}
      </div>

      {/* Dropdown results */}
      {open && (() => {
        const dropdownContent = (
          <div
            ref={dropdownRef}
            style={isInline ? undefined : {
              position: 'fixed',
              top: dropStyle.top,
              left: dropStyle.left,
              width: dropStyle.width,
              zIndex: 9999,
            }}
            className={`bg-white border border-mono-200 rounded-xl shadow-lg overflow-hidden animate-dropdown ${isInline ? 'mt-2' : ''}`}
          >
            <div className="max-h-[280px] overflow-y-auto overscroll-contain py-1 custom-scrollbar">
              {/* No query yet */}
              {!query.trim() && (
                <div className="px-4 py-6 text-center text-sm text-mono-400">
                  {t('common.enter_search')}
                </div>
              )}

              {/* Searching */}
              {query.trim() && searching && (
                <div className="px-4 py-6 text-center text-sm text-mono-400">
                  {t('common.searching')}
                </div>
              )}

              {/* Query but no results */}
              {query.trim() && !searching && results.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-mono-400">
                  {t('common.no_search_results')}
                </div>
              )}

              {/* Results */}
              {results.map((item, idx) => {
                const isSelected = value === item.address || value === item.name;
                return (
                  <button
                    key={`${idx}-${item.name}-${item.address}`}
                    type="button"
                    onClick={() => handleSelect(item)}
                    className={`w-full text-left px-4 py-3 transition-colors flex items-start gap-3 ${
                      isSelected
                        ? 'bg-orange-50 text-orange-500'
                        : 'text-mono-900 hover:bg-orange-50/50'
                    }`}
                  >
                    <Icon
                      name="place"
                      size={18}
                      className={`flex-shrink-0 mt-0.5 ${
                        isSelected ? 'text-orange-500' : 'text-mono-400'
                      }`}
                    />
                    <div className="min-w-0">
                      <div
                        className={`text-sm truncate ${
                          isSelected ? 'font-bold' : 'font-semibold'
                        }`}
                      >
                        {item.nameI18n || item.name}
                      </div>
                      <div
                        className={`text-xs mt-0.5 truncate ${
                          isSelected ? 'text-orange-400' : 'text-mono-400'
                        }`}
                      >
                        {item.addressI18n || item.address}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
        return isInline ? dropdownContent : createPortal(dropdownContent, document.body);
      })()}

      {/* Mobile fullscreen search */}
      {mobileFullscreen && createPortal(
        <div className="fixed inset-0 z-[9999] bg-white flex flex-col">
          <style>{`body { overflow: hidden; }`}</style>
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-mono-100">
            <button type="button" onClick={() => setMobileFullscreen(false)} className="p-1">
              <Icon name="chevron-left" size={22} className="text-mono-900" />
            </button>
            <input
              ref={mobileInputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={effectivePlaceholder}
              className="flex-1 text-sm font-medium text-mono-900 placeholder:text-mono-400 outline-none"
              autoFocus
            />
            {query && (
              <button type="button" onClick={() => { setQuery(''); setResults([]); }} className="p-1">
                <Icon name="close" size={18} className="text-mono-400" />
              </button>
            )}
          </div>
          {/* Results */}
          <div className="flex-1 overflow-y-auto">
            {!query.trim() && (
              <div className="px-4 py-10 text-center text-sm text-mono-400">{t('common.enter_search')}</div>
            )}
            {query.trim() && searching && (
              <div className="px-4 py-10 text-center text-sm text-mono-400">{t('common.searching')}</div>
            )}
            {query.trim() && !searching && results.length > 0 && (
              <div className="px-4 pt-3 pb-1 text-xs font-medium text-mono-400">{t('common.search_results_count', { count: results.length })}</div>
            )}
            {query.trim() && !searching && results.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-mono-400">{t('common.no_search_results')}</div>
            )}
            {results.map((item, idx) => (
              <button
                key={`m-${idx}-${item.name}`}
                type="button"
                onClick={() => {
                  if (restriction) {
                    const isSeoul = item.address.startsWith('서울');
                    const isGyeonggi = item.address.startsWith('경기');
                    const isIncheon = item.address.startsWith('인천');
                    const isGangwon = item.address.startsWith('강원');
                    let allowed = isSeoul;
                    if (restriction.allowGyeonggi) allowed = allowed || isGyeonggi;
                    if (restriction.allowIncheon) allowed = allowed || isIncheon;
                    if (restriction.allowGangwon) allowed = allowed || isGangwon;
                    if (!allowed) {
                      setMobileFullscreen(false);
                      setShowRestrictionModal(true);
                      return;
                    }
                  }
                  setQuery('');
                  // #105: input 표시용은 번역본, BE 전달값은 원본 한글 유지
                  setSelectedName(item.nameI18n || item.name);
                  setMobileFullscreen(false);
                  // #216: 3번째 인자로 현지화 명칭 전달(표시 전용). BE 전달값은 #105대로 한글 유지.
                  onChange?.(item.address, item.name, item.nameI18n || item.name);
                }}
                className="w-full text-left px-4 py-3.5 flex items-start gap-3 border-b border-mono-50 active:bg-orange-50/50"
              >
                <Icon name="place" size={18} className="text-mono-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-mono-900 truncate">{item.nameI18n || item.name}</div>
                  <div className="text-xs text-mono-400 mt-0.5 truncate">{item.addressI18n || item.address}</div>
                </div>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}

      {/* Restriction modal */}
      {showRestrictionModal && (
        <ModalWrapper onClose={() => setShowRestrictionModal(false)} variant="compact" gap="gap-4">
          <div className="flex items-center justify-center relative">
            <h2 className="text-lg font-bold text-mono-900">{t('common.guide')}</h2>
            <div className="absolute right-0">
              <ModalCloseButton onClick={() => setShowRestrictionModal(false)} />
            </div>
          </div>
          <p className="text-sm text-mono-700 text-center py-4">{t('common.region_inquiry')}</p>
          <div className="flex gap-3">
            <Button level="level3" fullWidth onClick={() => setShowRestrictionModal(false)}>{t('common.cancel')}</Button>
            <Button level="level1" fullWidth onClick={() => {
              setShowRestrictionModal(false);
              // BugHerd Vietnamese QA #5: /inquiry 진입 시 현재 부킹 페이지 경로를 기억해서
              // InquirySubmitted 에서 "예약 페이지로 돌아가기" 옵션을 노출 — 세션 silent drop 방지.
              try {
                sessionStorage.setItem('vvt_inquiry_return_to', window.location.pathname + window.location.search);
              } catch { /* sessionStorage 비활성 환경 — 무시 */ }
              navigate('/inquiry');
            }}>{t('common.confirm')}</Button>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
}
